import { Card, Row, Col, Form, Upload, Space, message } from "antd";
import { AntdButton, DatePickerField, InputField, SelectInput, TextareaField } from "../../../blueplanitUtils/InputField";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { AcceptOfferLetterAsync, DeclineOfferLetterAsync, SaveOfferDraft, saveOfferDraftAsync, SendOffer, sendOfferLetterAsync } from "../../../Slices/hiringSlice";

const OfferManagementData = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const selectedCandidate = useSelector((state) => state.hiring.selectedCandidate);
    const offer = useSelector((state) => state.hiring.offerDetails);
    const details = useSelector((state) => state.employee.AllEmployees);

    const [uploadedFileUrl, setUploadedFileUrl] = useState("");
    const [form, setForm] = useState({
        offeredCtc: "",
        joiningDate: "",
        probation: "",
        reportingManager: "",
        workLocation: "",
        workMode: "",
        noticePeriod: "",
        baseSalary: "",
        hra: "",
        allowances: "",
        variablePay: "",
        bonus: "",
        otherComp: "",
        issueDate: "",
        expiryDate: "",
        bgCheck: "Yes",
        offerStatus: "Draft",
        esignStatus: "Pending",
        remarks: "",
        totalCtc: ""
    });

    useEffect(() => {
        if (offer) {
            setForm({
                ...form,
                ...offer
            });
        }
    }, [offer]);

    const assignedToOptions = details.map((emp) => ({
        label: emp.firstname + " " + emp.lastname,
        value: emp.id,
    }));

    const handleNavigate = () => navigate(-1);

    const handleChange = (field, value) => {
        const updated = { ...form, [field]: value };
        if (["baseSalary", "hra", "allowances", "variablePay", "bonus", "otherComp"].includes(field)) {
            updated.totalCtc = calculateCTC(updated);
        }
        setForm(updated);
    };

    const calculateCTC = (v) =>
        Number(v.baseSalary || 0) +
        Number(v.hra || 0) +
        Number(v.allowances || 0) +
        Number(v.variablePay || 0) +
        Number(v.bonus || 0) +
        Number(v.otherComp || 0);

    // ---------- ACTION HANDLERS ----------
    const handleSaveDraft = async () => {
        try {
            const payload = {
                candidateId: selectedCandidate.candidateId,
                jobPostingId: selectedCandidate.jobPostingId,
                jobTitle: selectedCandidate.jobTitle,
                ...form,
                offeredCtc: Number(form.offeredCtc),
                baseSalary: Number(form.baseSalary),
                hra: Number(form.hra),
                allowances: Number(form.allowances),
                variablePay: Number(form.variablePay),
                bonus: Number(form.bonus),
                otherComp: Number(form.otherComp),
                totalCtc: Number(form.totalCtc),
                offerStatus: "In Progress"
            };

            await dispatch(saveOfferDraftAsync(payload));
            message.success("Offer draft saved successfully!");
        } catch (err) {
            console.error(err);
            message.error("Failed to save offer draft");
        }
    };

    const handleSendOffer = async () => {
        try {
            if (!uploadedFileUrl) return message.error("Please upload offer letter!");

            // Build the input object to match GraphQL OfferInput
            const input = {
                offerId: offer?.offerId, // optional if sending new offer
                offerLetterFile: uploadedFileUrl,
                candidateId: selectedCandidate.candidateId,
                requisitionId: selectedCandidate.requisitionId,
                jobTitle: selectedCandidate.jobTitle,
                offeredCtc: Number(form.offeredCtc),
                baseSalary: Number(form.baseSalary),
                hra: Number(form.hra),
                allowances: Number(form.allowances),
                variablePay: Number(form.variablePay),
                bonus: Number(form.bonus),
                otherComp: Number(form.otherComp),
                totalCtc: Number(form.totalCtc),
                joiningDate: form.joiningDate,
                probation: form.probation,
                reportingManager: form.reportingManager,
                workLocation: form.workLocation,
                workMode: form.workMode,
                noticePeriod: form.noticePeriod,
                issueDate: form.issueDate,
                expiryDate: form.expiryDate,
                bgCheck: form.bgCheck,
                offerStatus: "Sent",    // mark as sent
                esignStatus: "Pending",
                remarks: form.remarks || ""
            };

            await dispatch(sendOfferLetterAsync(input));

            message.success("Offer letter sent successfully!");
        } catch (err) {
            console.error(err);
            message.error("Failed to send offer");
        }
    };


    const handleMarkAccepted = async () => {
        try {
            if (!offer?.offerId) return message.error("Please save the offer first!");

            await dispatch(AcceptOfferLetterAsync({
                offerId: offer.offerId,
                offerStatus: "Accepted",
                esignStatus: "Signed"
            }));

            message.success("Offer marked as Accepted!");
        } catch (err) {
            console.error(err);
            message.error("Failed to update offer status");
        }
    };

    const handleDeclineOffer = async () => {
        try {
            if (!offer?.offerId) return message.error("Please save the offer first!");

            await dispatch(DeclineOfferLetterAsync({
                offerId: offer.offerId,
                offerStatus: "Declined"
            }));

            message.success("Offer marked as Declined!");
        } catch (err) {
            console.error(err);
            message.error("Failed to decline offer");
        }
    };

    // ---------- UPLOAD ----------
    const uploadProps = {
        beforeUpload: (file) => {
            const url = URL.createObjectURL(file);
            setUploadedFileUrl(url);
            return false;
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <div className="mb-2">
                    <AntdButton label="Back" type="primary" onClick={handleNavigate} />
                </div>

                <Card title="Offer Management" style={{ marginBottom: 20 }}>
                    <Card type="inner" title="Candidate Information" style={{ marginBottom: 20 }}>
                        <Row gutter={[16, 16]}>
                            <Col span={6}><b>Candidate ID:</b> {selectedCandidate?.candidateId}</Col>
                            <Col span={6}><b>Name:</b> {selectedCandidate?.firstName} {selectedCandidate?.lastName}</Col>
                            <Col span={6}><b>Position:</b> {selectedCandidate?.jobTitle}</Col>
                            <Col span={6}><b>Department:</b> {selectedCandidate?.department}</Col>
                            <Col span={6}><b>Email:</b> {selectedCandidate?.email}</Col>
                            <Col span={6}><b>Phone:</b> {selectedCandidate?.phone}</Col>
                        </Row>
                    </Card>

                    <Card type="inner" title="Offer Details" style={{ marginBottom: 20 }}>
                        <Row gutter={[16, 16]}>
                            <Col span={4}>
                                <InputField label="Offer ID" name="offerId" value={offer?.offerId || ""} disabled />
                            </Col>
                            <Col span={4}>
                                <InputField
                                    label="Offered CTC"
                                    value={form.offeredCtc}
                                    onChange={(e) => handleChange("offeredCtc", e.target.value)}
                                />
                            </Col>
                            <Col span={4}>
                                <InputField label="Total CTC" value={form.totalCtc} disabled />
                            </Col>
                            <Col span={4}>
                                <DatePickerField
                                    label="Joining Date"
                                    value={form.joiningDate}
                                    format="DD/MM/YYYY"
                                    onChange={(date) => handleChange("joiningDate", date)}
                                />
                            </Col>
                            <Col span={4}>
                                <InputField
                                    label="Probation Period"
                                    value={form.probation}
                                    onChange={(e) => handleChange("probation", e.target.value)}
                                />
                            </Col>
                            <Col span={4}>
                                <SelectInput
                                    label="Reporting Manager"
                                    name="reportingManager"
                                    value={form.reportingManager}
                                    options={assignedToOptions}
                                    onChange={(value) => handleChange("reportingManager", value)}
                                />
                            </Col>
                            <Col span={4}>
                                <InputField
                                    label="Work Location"
                                    value={form.workLocation}
                                    onChange={(e) => handleChange("workLocation", e.target.value)}
                                />
                            </Col>
                            <Col span={4}>
                                <SelectInput
                                    label="Work Mode"
                                    value={form.workMode}
                                    onChange={(value) => handleChange("workMode", value)}
                                    options={[
                                        { label: "Onsite", value: "Onsite" },
                                        { label: "Hybrid", value: "Hybrid" },
                                        { label: "Remote", value: "Remote" }
                                    ]}
                                />
                            </Col>
                            <Col span={4}>
                                <InputField
                                    label="Notice Period"
                                    value={form.noticePeriod}
                                    onChange={(e) => handleChange("noticePeriod", e.target.value)}
                                />
                            </Col>
                        </Row>
                    </Card>

                    <Card type="inner" title="Salary Breakdown" style={{ marginBottom: 20 }}>
                        <Row gutter={16}>
                            {["baseSalary", "hra", "allowances", "variablePay", "bonus", "otherComp"].map((field) => (
                                <Col span={4} key={field}>
                                    <InputField
                                        label={field.replace(/([A-Z])/g, " $1")}
                                        value={form[field]}
                                        onChange={(e) => handleChange(field, e.target.value)}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Card>

                    <Card type="inner" title="Offer Validity" style={{ marginBottom: 20 }}>
                        <Row gutter={16}>
                            <Col span={4}>
                                <DatePickerField
                                    label="Issue Date"
                                    value={form.issueDate}
                                    format="DD/MM/YYYY"
                                    onChange={(date) => handleChange("issueDate", date)}
                                />
                            </Col>
                            <Col span={4}>
                                <DatePickerField
                                    label="Expiry Date"
                                    value={form.expiryDate}
                                    format="DD/MM/YYYY"
                                    onChange={(date) => handleChange("expiryDate", date)}
                                />
                            </Col>
                            <Col span={5}>
                                <SelectInput
                                    label="Background Verification"
                                    value={form.bgCheck}
                                    onChange={(value) => handleChange("bgCheck", value)}
                                    options={[{ label: "Yes", value: "Yes" }, { label: "No", value: "No" }]}
                                />
                            </Col>
                            <Col span={5}>
                                <SelectInput
                                    label="Offer Status"
                                    value={form.offerStatus}
                                    onChange={(value) => handleChange("offerStatus", value)}
                                    options={[
                                        { label: "Draft", value: "Draft" },
                                        { label: "Sent", value: "Sent" },
                                        { label: "Accepted", value: "Accepted" },
                                        { label: "Declined", value: "Declined" }
                                    ]}
                                />
                            </Col>
                            <Col span={5}>
                                <SelectInput
                                    label="E-Signature Status"
                                    value={form.esignStatus}
                                    onChange={(value) => handleChange("esignStatus", value)}
                                    options={[
                                        { label: "Pending", value: "Pending" },
                                        { label: "Signed", value: "Signed" }
                                    ]}
                                />
                            </Col>
                        </Row>
                    </Card>

                    {/* Upload Offer Letter */}
                    <Card type="inner" title="Offer Letter PDF" style={{ marginBottom: 20 }}>
                        <Upload {...uploadProps}>
                            <AntdButton label="Upload Offer Letter" />
                        </Upload>
                        {uploadedFileUrl && <p style={{ color: "green" }}>PDF uploaded ✔</p>}
                    </Card>

                    <Card type="inner" title="Remarks" style={{ marginBottom: 20 }}>
                        <TextareaField
                            label="Remarks"
                            value={form.remarks}
                            onChange={(e) => handleChange("remarks", e.target.value)}
                            rows={3}
                        />
                    </Card>

                    {/* Actions */}
                    <Row justify="end">
                        <Space>
                            <AntdButton label="Save Draft" onClick={handleSaveDraft} />
                            <AntdButton label="Send Offer" type="primary" onClick={handleSendOffer} />
                            <AntdButton label="Mark as Accepted" type="default" variant="solid" color="warning" onClick={handleMarkAccepted} />
                            <AntdButton label="Decline Offer" type="default" variant="solid" color="danger" onClick={handleDeclineOffer} />
                        </Space>
                    </Row>
                </Card>
            </div>
        </div>
    );
};

export default OfferManagementData;
